/**
 * FAQ Type Definitions
 * Aligned with backend FAQ model for type safety and consistency
 */

export interface FAQCategory {
    _id: string
    name: string
    slug: string
    description?: string
    icon?: string
    color?: string
    isActive: boolean
    displayOrder: number
    createdAt: Date
    updatedAt: Date
}

export interface FAQ {
    _id: string
    question: string
    answer: string
    category: string // ObjectId reference to FAQCategory
    order: number
    views: number
    helpful: number
    published: boolean
    featured: boolean
    createdAt: Date
    updatedAt: Date
}

export interface FAQsResponse {
    success: boolean
    faqs: FAQ[]
    pagination: {
        currentPage: number
        totalPages: number
        totalItems: number
        itemsPerPage: number
    }
}

export interface FAQCategoriesResponse {
    success: boolean
    data: FAQCategory[]
}

export interface FAQFilters {
    page?: number
    limit?: number
    category?: string | 'all' // Now accepts category slug or 'all'
    search?: string
    sortBy?: 'newest' | 'helpful' | 'views'
}

export interface FAQDetailResponse {
    success: boolean
    faq: FAQ
}

export interface HelpfulResponse {
    success: boolean
    message: string
    helpful: number
}

export interface FeaturedFAQsResponse {
    success: boolean
    faqs: FAQ[]
}

export interface SearchFAQsResponse extends FAQsResponse { }

// Legacy hardcoded categories for fallback (will be replaced by dynamic data)
export const FAQ_CATEGORIES: Record<string, string> = {
    'getting-started': 'Getting Started',
    'payments': 'Payments',
    'account': 'Account',
    'safety': 'Safety',
    'platform': 'Platform'
}

export const FAQ_CATEGORY_COLORS: Record<string, string> = {
    'getting-started': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    'payments': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    'account': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    'safety': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    'platform': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
}

/**
 * Helper function to get category color or use a default
 */
export const getCategoryColor = (slug: string, colorHex?: string): string => {
    if (colorHex) {
        return `style="background-color: ${colorHex}; color: white;"`
    }
    return FAQ_CATEGORY_COLORS[slug] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
}